import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * What a `client_contact` record is to the people model. `tenant_staff`: the
 * tenant's own employee entered as a customer contact (left out of people).
 * `placeholder`: "WILL CALL", a blank name and the like (not a person).
 */
export type ContactRecordKind = 'person' | 'department' | 'placeholder' | 'tenant_staff';

/** How the record got its person, for explaining a link and for undo. */
export type ContactRecordMatchMethod =
	| 'new'
	| 'same_customer'
	| 'same_family'
	| 'email'
	| 'phone'
	| 'manual';

/**
 * Maps one `client_contact` record to its person and keeps the values that
 * record had the last time Workd looked at it (PD-17064). Matching compares a
 * record only with its own previous values: a record that still holds an old
 * value is not news and never overwrites the person; a record whose value
 * changed is (plan section 1, M3).
 *
 * Kept in its own table so matching never writes `client_contact` (see
 * ContactEntity). `isPinned` records a manual move or split; matching leaves a
 * pinned record alone while its name and email stay what they were when it was
 * pinned.
 *
 * Not auditable: one row per contact record, rewritten by the background sweep.
 */
@Index('contact_record__fk_client_contact', ['fkClientContactId'], { unique: true })
@Index('contact_record__fk_contact', ['fkContactId'], {})
@Index('contact_record__fk_client', ['fkClientId'], {})
@Entity('contact_record', {}, { auditable: false })
export class ContactRecordEntity extends BaseEntity {
	@Column('int', { name: 'fk_client_contact__id', unsigned: true })
	public fkClientContactId!: number;

	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public fkContactId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('varchar', { name: 'recordKind', length: 32 })
	public recordKind!: ContactRecordKind;

	@Column('varchar', { name: 'matchMethod', length: 32, nullable: true })
	public matchMethod!: ContactRecordMatchMethod | null;

	@Column({ type: 'boolean', name: 'isPinned', width: 1, default: () => "'0'" })
	public isPinned!: boolean;

	/** False once the record is deleted at its source. */
	@Column({ type: 'boolean', name: 'isLive', width: 1, default: () => "'1'" })
	public isLive!: boolean;

	// Values as last seen, after clean-up (see contact-normalization.util).
	@Column('varchar', { name: 'seenName', length: 255, nullable: true })
	public seenName!: string | null;

	@Column('varchar', { name: 'seenEmail', length: 255, nullable: true })
	public seenEmail!: string | null;

	@Column('varchar', { name: 'seenPhone', length: 64, nullable: true })
	public seenPhone!: string | null;

	@Column('varchar', { name: 'seenMobilePhone', length: 64, nullable: true })
	public seenMobilePhone!: string | null;

	@Column('varchar', { name: 'seenTitle', length: 255, nullable: true })
	public seenTitle!: string | null;

	/** The record's `_modified_date` when last processed. */
	@Column('datetime', { name: 'seenModifiedDate', nullable: true })
	public seenModifiedDate!: Date | null;
}
